/**
 * Product config: types, list, game tags, categories, and cover URLs.
 * Covers live in public/_static/covers (e.g. semacro.mp4, apple.mp4).
 */

export type GameTag = "ALL_GAMES" | "PUBG" | "VALORANT" | "CS2";

export type ProductCategory = "macro" | "aimbot" | "cheats";

export type MediaType = "image" | "gif" | "video";

/** Plan-based pricing: keys are plan ids (e.g. day, week, month, lifetime), values are prices. */
export type PricePlans = Record<string, number>;

export interface Product {
  id: string;
  name: string;
  description?: string;
  category: ProductCategory;
  gameTag: GameTag;
  /** URL for card media (image/gif/video). Use getCoverUrl() or path from public. */
  mediaUrl: string;
  mediaType: MediaType;
  /** Single price when no pricePlans. Ignored when pricePlans is set. */
  price: number;
  /** When set, product uses plan switcher. Keys: plan id (day, week, month, lifetime), values: price. */
  pricePlans?: PricePlans;
  /** Default selected plan key when product has pricePlans (e.g. "month", "day"). */
  defaultPlan?: string;
  isFeatured?: boolean;
}

const COVERS_BASE = "/api/media/covers";

/** Map product id (or cover key) to filename in public/_static/covers. No entry = no media link (placeholder shown). */
const COVERS: Record<string, string> = {
  "macro-1": "semacro.mp4",
  "macro-2": "usbmacro.mp4",
  "macro-3": "80loot.mp4",
  "macro-4": "fastloot.mp4",
  "macro-5": "cs2.mp4",
  "cheats-2": "apple.mp4",
};

/** Placeholder when product has no cover (aimbot, batman, etc.). */
const NO_PREVIEW_PLACEHOLDER =
  "data:image/svg+xml," +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="400" height="300" viewBox="0 0 400 300"><rect fill="%231e293b" width="400" height="300"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="%2394a3b8" font-family="sans-serif" font-size="16">No preview</text></svg>'
  );

export function getCoverUrl(productId: string): string {
  const filename = COVERS[productId];
  if (!filename) return NO_PREVIEW_PLACEHOLDER;
  return `${COVERS_BASE}/${filename}`;
}

export const gameTags: Record<GameTag, string> = {
  ALL_GAMES: "All games",
  PUBG: "PUBG",
  VALORANT: "Valorant",
  CS2: "CS2",
};

export const productCategories: Record<ProductCategory, string> = {
  macro: "Macro",
  aimbot: "Aimbot",
  cheats: "Cheats",
};

